import React, { useState, useEffect } from 'react';
import {
  numbersApi,
  twilioApi,
  TrackingNumber,
  CallRailPreviewResult,
  CallRailCommitResult,
} from '../services/api';
import './Numbers.css';

const Numbers: React.FC = () => {
  const [numbers, setNumbers] = useState<TrackingNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCallRailImport, setShowCallRailImport] = useState(false);
  const [editingNumber, setEditingNumber] = useState<TrackingNumber | null>(null);

  // CallRail import modal state. The API key/account ID are only ever held
  // here, for the modal's lifetime, and are cleared by closeCallRailImport
  // whenever the modal goes away (success, cancel, or dismissal) — never
  // written to localStorage/sessionStorage or put in a URL.
  const [crApiKey, setCrApiKey] = useState('');
  const [crAccountId, setCrAccountId] = useState('');
  const [importPreview, setImportPreview] = useState<CallRailPreviewResult | null>(null);
  const [importResult, setImportResult] = useState<CallRailCommitResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    campaignName: '',
    forwardTo: '',
    areaCode: '',
    recordCalls: true,
    whisperEnabled: true
  });

  useEffect(() => {
    fetchNumbers();
  }, []);

  const fetchNumbers = async () => {
    try {
      setLoading(true);
      const response = await numbersApi.getNumbers();
      setNumbers(response.data);
    } catch (error) {
      console.error('Error fetching numbers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingNumber) {
        await numbersApi.updateNumber(editingNumber.id, {
          campaignName: formData.campaignName,
          forwardTo: formData.forwardTo,
          isActive: editingNumber.is_active,
          recordCalls: formData.recordCalls,
          whisperEnabled: formData.whisperEnabled
        });
      } else if (formData.areaCode) {
        await twilioApi.buyNumber({
          areaCode: formData.areaCode,
          campaignName: formData.campaignName,
          forwardTo: formData.forwardTo,
          recordCalls: formData.recordCalls,
          whisperEnabled: formData.whisperEnabled
        });
      } else {
        await numbersApi.createNumber({
          phoneNumber: formData.phoneNumber,
          campaignName: formData.campaignName,
          forwardTo: formData.forwardTo,
          recordCalls: formData.recordCalls,
          whisperEnabled: formData.whisperEnabled
        });
      }

      setFormData({
        phoneNumber: '',
        campaignName: '',
        forwardTo: '',
        areaCode: '',
        recordCalls: true,
        whisperEnabled: true
      });
      setShowAddForm(false);
      setEditingNumber(null);
      fetchNumbers();
    } catch (error) {
      console.error('Error saving number:', error);
      alert('Error saving number. Please try again.');
    }
  };

  const handleEdit = (number: TrackingNumber) => {
    setEditingNumber(number);
    setFormData({
      phoneNumber: number.phone_number,
      campaignName: number.campaign_name,
      forwardTo: number.forward_to ?? '',
      areaCode: '',
      recordCalls: number.record_calls,
      whisperEnabled: number.whisper_enabled
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this number?')) {
      try {
        await numbersApi.deleteNumber(id);
        fetchNumbers();
      } catch (error) {
        console.error('Error deleting number:', error);
        alert('Error deleting number. Please try again.');
      }
    }
  };

  const toggleActive = async (number: TrackingNumber) => {
    try {
      await numbersApi.updateNumber(number.id, {
        campaignName: number.campaign_name,
        forwardTo: number.forward_to,
        isActive: !number.is_active,
        recordCalls: number.record_calls,
        whisperEnabled: number.whisper_enabled
      });
      fetchNumbers();
    } catch (error) {
      console.error('Error updating number:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      phoneNumber: '',
      campaignName: '',
      forwardTo: '',
      areaCode: '',
      recordCalls: true,
      whisperEnabled: true
    });
    setShowAddForm(false);
    setEditingNumber(null);
  };

  // Closes the CallRail import modal and clears every piece of its state,
  // including the API key — called on success, cancel, and dismissal alike.
  const closeCallRailImport = () => {
    // A commit that already landed has to refresh the table on the way out,
    // whichever control dismisses the modal. "Done" did this; the × did not,
    // so dismissing the result panel that way left the operator looking at the
    // pre-import list — none of the numbers they had just been told were
    // created. Their entire safety model is "I can see the numbers", so that
    // reads as a failed import.
    const committed = importResult !== null;
    setShowCallRailImport(false);
    setCrApiKey('');
    setCrAccountId('');
    setImportPreview(null);
    setImportResult(null);
    setImportError(null);
    setPreviewing(false);
    setCommitting(false);
    if (committed) fetchNumbers();
  };

  const handlePreviewImport = async () => {
    setImportError(null);
    setPreviewing(true);
    try {
      const response = await twilioApi.importCallRail({ apiKey: crApiKey, accountId: crAccountId });
      setImportPreview(response.data as CallRailPreviewResult);
    } catch (error: any) {
      setImportError(error.response?.data?.error || 'Failed to preview the CallRail import. Please try again.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmImport = async () => {
    setImportError(null);
    setCommitting(true);
    try {
      const response = await twilioApi.importCallRail({
        apiKey: crApiKey,
        accountId: crAccountId,
        commit: true,
      });
      // The commit response is the operator's ONLY confirmation that the 15
      // numbers and ~152 calls actually landed, and it carries commit-time
      // warnings the preview could not know about. It used to be awaited and
      // discarded, so a commit that created nothing looked identical to one
      // that created everything.
      //
      // The modal stays open to show it, so the credentials are cleared here
      // rather than waiting for closeCallRailImport — the key lives in this
      // component's state for the shortest time that still works.
      setCrApiKey('');
      setCrAccountId('');
      setImportPreview(null);
      setImportResult(response.data as CallRailCommitResult);
      setCommitting(false);
    } catch (error: any) {
      setImportError(error.response?.data?.error || 'Failed to import from CallRail. Please try again.');
      setCommitting(false);
    }
  };

  // closeCallRailImport now refetches whenever a commit landed, so this is
  // just the named control for the same exit.
  const handleFinishImport = () => {
    closeCallRailImport();
  };

  // The only way to take an imported number off the pending list. Until this
  // existed the rollout was unreachable from the app: the badge was inert and
  // neither handleSubmit nor toggleActive ever sent pendingPort, so once the
  // carrier ports landed the owner could not activate any of the 15 imported
  // numbers without hand-crafted PUTs against production.
  //
  // It asserts a real-world fact — that the carrier port has completed — so it
  // is confirmed rather than being a one-click toggle. The server accepts both
  // fields in a single PUT and its activation guard passes because
  // pendingPort: false is explicit in that same request.
  const markPortComplete = async (number: TrackingNumber) => {
    if (!number.forward_to) {
      alert(
        'Set a "Forward To" destination for this number first — activating it with ' +
          'nowhere to forward would present a working number that never rings.'
      );
      return;
    }

    const confirmed = window.confirm(
      `Has the carrier port for ${formatPhoneNumber(number.phone_number)} completed?\n\n` +
        'Only confirm this once the number is actually live on our carrier. ' +
        'Confirming marks the port complete and activates the number, so calls ' +
        'to it will be routed here immediately.'
    );
    if (!confirmed) return;

    try {
      await numbersApi.updateNumber(number.id, {
        campaignName: number.campaign_name,
        forwardTo: number.forward_to,
        pendingPort: false,
        isActive: true,
      });
      fetchNumbers();
    } catch (error: any) {
      console.error('Error marking port complete:', error);
      alert(error.response?.data?.error || 'Could not mark the port complete. Please try again.');
    }
  };

  // forward_to is null for a number that has been imported but not yet
  // pointed anywhere. That is deliberate — an imported number must not be able
  // to ring until someone chooses a destination — so this has to render it
  // rather than throw. It previously assumed a string, because the old
  // boot-time sync always wrote a placeholder destination into every row.
  const formatPhoneNumber = (phoneNumber: string | null | undefined) => {
    if (!phoneNumber) return '—';
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
    }
    return phoneNumber;
  };

  return (
    <div className="numbers-page">
      <div className="numbers-header">
        <h1>Tracking Numbers</h1>
        <div className="header-buttons">
          <button
            onClick={() => setShowCallRailImport(true)}
            className="import-btn"
          >
            📥 Import from CallRail
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="add-number-btn"
          >
            + Add Number
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="add-form-overlay">
          <div className="add-form-modal">
            <div className="add-form-header">
              <h3>{editingNumber ? 'Edit Number' : 'Add New Number'}</h3>
              <button onClick={resetForm} className="close-btn">×</button>
            </div>

            <form onSubmit={handleSubmit} className="add-form">
              <div className="form-group">
                <label>Campaign Name</label>
                <input
                  type="text"
                  value={formData.campaignName}
                  onChange={(e) => setFormData(prev => ({ ...prev, campaignName: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label>Forward To</label>
                <input
                  type="tel"
                  value={formData.forwardTo}
                  onChange={(e) => setFormData(prev => ({ ...prev, forwardTo: e.target.value }))}
                  placeholder="+1234567890"
                  required
                />
              </div>

              <div className="form-row">
                <label htmlFor="recordCalls">
                  <input
                    id="recordCalls"
                    type="checkbox"
                    checked={formData.recordCalls}
                    onChange={(e) => setFormData(prev => ({ ...prev, recordCalls: e.target.checked }))}
                  />
                  {' '}Record calls on this number
                </label>
              </div>

              <div className="form-row">
                <label htmlFor="whisperEnabled">
                  <input
                    id="whisperEnabled"
                    type="checkbox"
                    checked={formData.whisperEnabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, whisperEnabled: e.target.checked }))}
                  />
                  {' '}Announce the campaign name when I answer (whisper)
                </label>
              </div>

              {!editingNumber && (
                <>
                  <div className="form-group">
                    <label>Buy New Number by Area Code</label>
                    <input
                      type="text"
                      value={formData.areaCode}
                      onChange={(e) => setFormData(prev => ({ ...prev, areaCode: e.target.value }))}
                      placeholder="212"
                      maxLength={3}
                    />
                    <small>Leave empty to add existing number manually</small>
                  </div>

                  <div className="form-group">
                    <label>Or Enter Existing Number</label>
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                      placeholder="+1234567890"
                      disabled={!!formData.areaCode}
                    />
                  </div>
                </>
              )}

              <div className="form-buttons">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingNumber ? 'Update' : 'Add'} Number
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCallRailImport && (
        <div className="add-form-overlay">
          <div className={`add-form-modal ${importPreview ? 'import-modal' : ''}`}>
            <div className="modal-header">
              <h2>Import Numbers from CallRail</h2>
              <button
                className="close-btn"
                onClick={closeCallRailImport}
              >
                ×
              </button>
            </div>

            <div className="import-content">
              {importError && (
                <div className="import-error">{importError}</div>
              )}

              {importResult ? (
                <>
                  <p className="import-step-intro">
                    Imported from <strong>{importResult.accountName}</strong>.
                  </p>

                  <ul className="import-result-list">
                    <li><strong>{importResult.numbersCreated}</strong> number(s) created</li>
                    <li><strong>{importResult.numbersUpdated}</strong> number(s) updated</li>
                    <li><strong>{importResult.callsImported}</strong> call(s) imported</li>
                  </ul>

                  <p className="import-step-intro">
                    New numbers are staged as <strong>Pending port</strong> and cannot ring
                    here until their carrier port completes. Use “Port complete” on each row
                    once it does.
                  </p>

                  {importResult.warnings.length > 0 && (
                    <div className="import-warnings">
                      <strong>Warnings</strong>
                      <ul>
                        {importResult.warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="form-buttons">
                    <button type="button" onClick={handleFinishImport} className="save-btn">
                      Done
                    </button>
                  </div>
                </>
              ) : !importPreview ? (
                <>
                  <p className="import-step-intro">
                    Enter your CallRail API key and account ID to preview which numbers
                    and calls will be imported. Nothing is changed until you confirm.
                  </p>

                  <div className="form-group">
                    <label>CallRail API Key</label>
                    <input
                      type="password"
                      value={crApiKey}
                      onChange={(e) => setCrApiKey(e.target.value)}
                      autoComplete="off"
                    />
                  </div>

                  <div className="form-group">
                    <label>Account ID</label>
                    <input
                      type="text"
                      value={crAccountId}
                      onChange={(e) => setCrAccountId(e.target.value)}
                    />
                  </div>

                  <div className="form-buttons">
                    <button
                      type="button"
                      onClick={closeCallRailImport}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handlePreviewImport}
                      className="import-preview-btn"
                      disabled={previewing || !crApiKey || !crAccountId}
                    >
                      {previewing ? 'Previewing…' : 'Preview import'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="import-step-intro">
                    Previewing import from <strong>{importPreview.accountName}</strong>.
                    Review the numbers below, then confirm to import them.
                  </p>

                  <div className="numbers-table-container">
                    <table className="numbers-table">
                      <thead>
                        <tr>
                          <th>Number</th>
                          <th>Name</th>
                          <th>Destination</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.numbers.map((num) => (
                          <tr key={num.trackingNumber}>
                            <td className="phone-number">{num.trackingNumber}</td>
                            <td>{num.name}</td>
                            {/* The destination the commit will ACTUALLY write.
                                A forward_to already set here wins, so showing
                                CallRail's value unconditionally made this
                                preview a promise the commit would break. */}
                            <td>
                              {num.resultingDestination || 'none'}
                              {num.keepsLocalForwardTo && (
                                <span className="import-destination-note">
                                  Kept — set here already. CallRail&apos;s{' '}
                                  {num.destinationNumber || 'none'} will not overwrite it.
                                </span>
                              )}
                            </td>
                            <td>{num.action}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <p className="import-summary">
                    {importPreview.callsToImport} call(s) will be imported.
                  </p>

                  {importPreview.warnings.length > 0 && (
                    <div className="import-warnings">
                      <strong>Warnings</strong>
                      <ul>
                        {importPreview.warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="form-buttons">
                    <button
                      type="button"
                      onClick={closeCallRailImport}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmImport}
                      className="import-confirm-btn"
                      disabled={committing}
                    >
                      {committing ? 'Importing…' : 'Confirm import'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading numbers...</div>
      ) : (
        <div className="numbers-table-container">
          <table className="numbers-table">
            <thead>
              <tr>
                <th>Phone Number</th>
                <th>Campaign</th>
                <th>Forward To</th>
                <th>Status</th>
                <th>Recording</th>
                <th>Whisper</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {numbers.map((number) => (
                <tr key={number.id}>
                  <td className="phone-number">{formatPhoneNumber(number.phone_number)}</td>
                  <td>{number.campaign_name}</td>
                  <td>{formatPhoneNumber(number.forward_to)}</td>
                  <td>
                    {number.pending_port ? (
                      <div className="port-pending">
                        <span className="port-badge">Pending port</span>
                        <button
                          onClick={() => markPortComplete(number)}
                          className="port-complete-btn"
                          title="The carrier port for this number has completed — activate it here."
                        >
                          Port complete
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => toggleActive(number)}
                        className={`status-toggle ${number.is_active ? 'active' : 'inactive'}`}
                      >
                        {number.is_active ? 'Active' : 'Inactive'}
                      </button>
                    )}
                  </td>
                  <td>{number.record_calls ? 'On' : 'Off'}</td>
                  <td>{number.whisper_enabled ? 'On' : 'Off'}</td>
                  <td>{new Date(number.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(number)}
                        className="edit-btn"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(number.id)}
                        className="delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Numbers;